import { createAsyncThunk, createSlice, current, ActionReducerMapBuilder } from "@reduxjs/toolkit";
import { PayloadAction } from "@reduxjs/toolkit";
import { SendCreateWcClaimCommand } from "../api/claims/createWcClaim";
import { compareKeys, generateAmazonClaimReferenceId } from "src/forms/tools";
import { ClaimSubmittedStatus, LineOfBusiness, type ResetFormModalContents } from "./types";
import {
  ProcessedApiResponse,
  ResponseField,
  SERVER_ERROR_RESPONSES,
  HttpStatusCodes,
} from "src/api/types";
import { getWcClaimService } from "src/api/claims/getWcClaimService";
import { CreateWcClaimCommandInput } from "@amzn/ttechclaimintakeservice-client";

// Define a type for the slice state
interface AppState {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Index signature enables dynamic setValueInAppState pattern
  [index: string]: any;

  amazonClaimReferenceId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Claim response shape varies by TPA and is not typed in SDK
  duplicateClaim: { [key: string]: any } | null;
  duplicateClaimChecked?: boolean;
  createWcClaimResponse?: ProcessedApiResponse;
  submitScreenStatus?: ClaimSubmittedStatus;
  triggerOpenResetFormModal?: boolean;
  remoteResetFormModalContents: ResetFormModalContents;
  requestAppReset: boolean;
  appLoaded?: boolean;
  invalidStateModalOpen: boolean;
  stateIsValid: boolean;
  tpaMaintenance?: boolean;
  lineOfBusiness: LineOfBusiness;
  loadingIPLClaim?: boolean;
}
export const initialState: AppState = {
  duplicateClaim: null,
  duplicateClaimChecked: false,
  submitScreenStatus: ClaimSubmittedStatus.LOADING,
  amazonClaimReferenceId: generateAmazonClaimReferenceId(),
  requestAppReset: false,
  triggerOpenResetFormModal: false,
  remoteResetFormModalContents: {
    title: "Reset Form",
    description: "The form will be reset, are you sure you want to continue?",
    cancel: "No",
    confirm: "Yes, Reset Form",
  },
  appLoaded: false,
  invalidStateModalOpen: false,
  stateIsValid: false,
  tpaMaintenance: false,
  lineOfBusiness: LineOfBusiness.WORKERS_COMPENSATION,
  loadingIPLClaim: false,
};

export const verifyStateIsValid = (state: AppState) => {
  return compareKeys(state, initialState);
};

export const getWcClaim = createAsyncThunk(
  "app/getWcClaim",
  async (params: { associateEmployeeId: string; incidentDate: string }, { rejectWithValue }) => {
    const { associateEmployeeId, incidentDate } = params;
    try {
      const getClaimResponse = await getWcClaimService({
        associateEmployeeId,
        incidentDate,
      });
      return getClaimResponse;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const createWcClaim = createAsyncThunk(
  "app/createWcClaim",
  async (params: { createWcClaimInput: CreateWcClaimCommandInput }, { rejectWithValue }) => {
    const { createWcClaimInput } = params;

    try {
      const response = await SendCreateWcClaimCommand(createWcClaimInput);
      return response;
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);
export const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setValueInAppState: (
      state: AppState,
      action: PayloadAction<{
        key: string;
        value: AppState[keyof AppState];
      }>
    ) => {
      const { key, value } = action.payload;
      state[key as keyof AppState] = value;
    },
    resetAppState: () => {
      return {
        ...initialState,
        amazonClaimReferenceId: generateAmazonClaimReferenceId(),
      };
    },
    requestAppReset: (state: AppState) => {
      state.requestAppReset = true;
    },
    setLineOfBusiness: (state: AppState, action: PayloadAction<LineOfBusiness>) => {
      state.lineOfBusiness = action.payload;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<AppState>) => {
    // Add reducers for additional action types here, and handle loading state as needed
    builder
      .addCase(getWcClaim.fulfilled, (state: AppState, action) => {
        state.duplicateClaimChecked = true;
        if (action.payload?.claimNumber) {
          state.duplicateClaim = action.payload as unknown as { [key: string]: unknown };
        }
      })
      .addCase(getWcClaim.rejected, (state: AppState) => {
        state.duplicateClaimChecked = true;
      })
      .addCase(createWcClaim.fulfilled, (state: AppState, action) => {
        state.createWcClaimResponse = action.payload;
        state.submitScreenStatus = ClaimSubmittedStatus.SUCCESS;
      })
      .addCase(createWcClaim.pending, (state: AppState) => {
        state.submitScreenStatus = ClaimSubmittedStatus.LOADING;
      })
      .addCase(createWcClaim.rejected, (state: AppState, action) => {
        state.submitScreenStatus = ClaimSubmittedStatus.ERROR;
        const data: ResponseField[] = [];
        const { message, errors, $metadata, warningList } = action.payload as Record<
          string,
          unknown
        >;
        const statusCode = ($metadata as Record<string, unknown>)?.httpStatusCode?.toString();
        if (SERVER_ERROR_RESPONSES.includes(statusCode as HttpStatusCodes)) {
          data.push({
            fieldName: "Amazon Claim Reference ID",
            fieldValue: current(state).amazonClaimReferenceId,
          });
        }

        if ((errors as ResponseField[])?.length) {
          data.push(...(errors as ResponseField[]));
        }
        const processedResponse: ProcessedApiResponse = {
          data,
          statusCode: statusCode as string | undefined,
          warningList: warningList as string[] | undefined,
          message: message as string | undefined,
        };
        state.createWcClaimResponse = processedResponse;
      });
  },
});

export const { setValueInAppState, resetAppState, requestAppReset, setLineOfBusiness } =
  appSlice.actions;

export default appSlice.reducer;
